/**
 * A signed change that says what it is compared against.
 *
 * `baseline` is required. A delta with no baseline is a number with a sign on
 * it: −4.2% against yesterday, against a benchmark and against a typical week
 * are three different facts, and only the call site knows which one it has.
 *
 * `goodDirection` is declared, never inferred from the sign — −19% emissions
 * is good and −19% portfolio is not — and exactly zero is Neutral, never
 * Positive. Colour belongs to the pill; the text says the same thing unaided,
 * so a reader who cannot separate the two hues loses nothing.
 */
"use client";
import type { ReactElement } from "react";
import { formatMoneyFigure } from "./cresultcard.money";
import {
  CRC_FIGURE_SIZE,
  CRC_NUMERIC_STYLE,
  CRC_TEXT_MUTED,
  CRC_TONE_COLOR,
} from "./cresultcard.tokens";
import {
  CresultcardFigureScale,
  CresultcardGoodDirection,
  CresultcardTone,
} from "./cresultcard.types";

const SIGN_POSITIVE = "+";
const SIGN_NEGATIVE = "−";
const PERCENT_SUFFIX = "%";
const PILL_TINT_BY_TONE: Record<CresultcardTone, string> = {
  [CresultcardTone.Positive]: "rgba(102,254,193,0.1)",
  [CresultcardTone.Negative]: "rgba(255,110,132,0.1)",
  [CresultcardTone.Caution]: "rgba(242,159,87,0.1)",
  [CresultcardTone.Neutral]: "rgba(255,255,255,0.06)",
  [CresultcardTone.Muted]: "rgba(255,255,255,0.05)",
};

interface CresultcardDeltaProps {
  /**
   * The change as a percentage, already signed. When BOTH this and `amount`
   * are given, the amount leads and the percentage rides the pill beside it —
   * the one-line "absolute change plus percentage" every quote surface wants,
   * which used to have to be assembled separately by each pillar because only
   * one of the two was ever rendered.
   */
  readonly percent?: number;
  /** The change as an absolute amount, already signed. */
  readonly amount?: number;
  /** What it changed against, in words: `than typical`, `vs benchmark`. */
  readonly baseline: string;
  readonly unit?: string;
  readonly goodDirection: CresultcardGoodDirection;
  /** Rounding is an explicit, per-call decision, and this is the only one. */
  readonly precision: number;
  readonly scale: CresultcardFigureScale;
}

function toneOf(
  value: number,
  goodDirection: CresultcardGoodDirection,
): CresultcardTone {
  if (value === 0) {
    return CresultcardTone.Neutral;
  }
  const rising = value > 0;
  const good =
    goodDirection === CresultcardGoodDirection.Up ? rising : !rising;
  return good ? CresultcardTone.Positive : CresultcardTone.Negative;
}

function signedText(value: number, precision: number): string {
  const magnitude = formatMoneyFigure({
    amount: Number(Math.abs(value).toFixed(precision)),
  });
  if (value === 0) {
    return magnitude;
  }
  return `${value > 0 ? SIGN_POSITIVE : SIGN_NEGATIVE}${magnitude}`;
}

export function CresultcardDelta({
  percent,
  amount,
  baseline,
  unit,
  goodDirection,
  precision,
  scale,
}: CresultcardDeltaProps): ReactElement | null {
  const value = percent ?? amount;
  if (value == null) {
    return null;
  }

  const tone = toneOf(value, goodDirection);
  const size = CRC_FIGURE_SIZE[scale];
  const suffix = percent != null ? PERCENT_SUFFIX : (unit ?? "");
  // Both halves were given, so the absolute change leads in plain figures and
  // the pill carries the percentage. One of the two alone is unchanged.
  const leadAmount = percent != null && amount != null ? amount : null;

  return (
    <span
      data-el="Cresultcard/Delta/Root"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        minWidth: 0,
      }}
    >
      {leadAmount != null && (
        <span
          style={{
            ...CRC_NUMERIC_STYLE,
            fontSize: Math.max(10, Math.round(size * 0.62)),
            fontWeight: 700,
            lineHeight: 1,
            whiteSpace: "nowrap",
            color: CRC_TONE_COLOR[tone],
          }}
        >
          {`${signedText(leadAmount, precision)}${unit ?? ""}`}
        </span>
      )}
      <span
        style={{
          ...CRC_NUMERIC_STYLE,
          paddingBlock: 3,
          paddingInline: 7,
          borderRadius: 999,
          fontSize: Math.max(10, Math.round(size * 0.62)),
          fontWeight: 740,
          lineHeight: 1,
          whiteSpace: "nowrap",
          color: CRC_TONE_COLOR[tone],
          backgroundColor: PILL_TINT_BY_TONE[tone],
        }}
      >
        {`${signedText(value, precision)}${suffix}`}
      </span>
      <span
        style={{
          fontSize: 9.5,
          fontWeight: 540,
          lineHeight: 1.2,
          color: CRC_TEXT_MUTED,
        }}
      >
        {baseline}
      </span>
    </span>
  );
}
